import React, { useCallback, useState } from "react";
import { Button, Spin } from "antd";
import jsPDF from "jspdf";
import { DownloadOutlined } from "@ant-design/icons";
import {
    updateCommonErrorMessage,
    updateCommonSuccessMessage,
    updateDownloadAllLoading
} from "@/redux/actions/climateShiftAction";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/reducer/store";
import syngentaImg from "@/resource/images/png/climateShift/syngenta.png";

interface PdfExportDemoProps {
    histogramRef: React.RefObject<any>;
    sup15CRef: React.RefObject<any>;
    sub2CRef: React.RefObject<any>;
    activeTab: string;
    onTabChange?: (key: string) => void;
}

const PdfExportDemo: React.FC<PdfExportDemoProps> = ({ histogramRef, sup15CRef, sub2CRef, activeTab, onTabChange }) => {
    const dispatch = useDispatch();
    const chartResult = useSelector((state: RootState) => state.climateShiftReducer.chartResult);
    const selectedLocationData = useSelector((state: RootState) => state.climateShiftReducer.selectedLocationData);
    const selectedLocationDataForAverage = useSelector((state: RootState) => state.climateShiftReducer.selectedLocationDataForAverage);
    const selectedCountry = useSelector((state: RootState) => state.climateShiftReducer.country);

    const [loading, setLoading] = useState(false);

    // 当 selectedLocationData 或 selectedLocationDataForAverage 变化时，按钮加载1分钟
    React.useEffect(() => {
        setLoading(true);
        const timer = setTimeout(() => {
            setLoading(false);
        }, 20000);
        return () => clearTimeout(timer);
    }, [selectedLocationData, selectedLocationDataForAverage]);

    const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

    const handleDownloadPdf = useCallback(async () => {
        //setLoading(true);
        dispatch(updateDownloadAllLoading(true));
        const originalTab = activeTab;
        try {
            // 1. 切到tab1，等待渲染
            if (onTabChange) onTabChange("1");
            await sleep(400); // 等待tab内容渲染
            let imgData1 = null;
            if (histogramRef?.current && chartResult?.[0]?.histogramOfYears?.length > 0) {
                const lastYearIndex = chartResult[0].histogramOfYears.length - 1;
                if (histogramRef.current.getChartImage) {
                    imgData1 = await histogramRef.current.getChartImage(lastYearIndex);
                }
            }
            // 2. 切到tab2，等待渲染
            if (onTabChange) onTabChange("2");
            await sleep(400);
            let imgData2 = null, imgData3 = null;
            if (sup15CRef?.current && sup15CRef.current.getChartImage) {
                imgData2 = await sup15CRef.current.getChartImage();
            }
            if (sub2CRef?.current && sub2CRef.current.getChartImage) {
                imgData3 = await sub2CRef.current.getChartImage();
            }
            // 3. 恢复原tab
            if (onTabChange && originalTab) onTabChange(originalTab);
            // 4. 生成PDF
            const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
            const today = new Date();
            const pad = (n: number) => n < 10 ? "0" + n : n;
            const dateStr = `${pad(today.getDate())}-${pad(today.getMonth() + 1)}-${today.getFullYear()}`;
            // 页脚函数
            const addFooter = (pageNum: number) => {
                // 页脚分割线
                pdf.setDrawColor(180);
                pdf.setLineWidth(0.3);
                pdf.line(15, 280, 195, 280); // 横线

                // 日期居中
                pdf.setFontSize(10);
                pdf.setTextColor(136, 136, 136); // #888 深灰色，比黑色淡
                pdf.setFont("Arial", "normal");
                pdf.text(dateStr, 105, 288, { align: "center" });
                pdf.setTextColor(0); // 恢复为黑色，避免影响后续内容

                // 页码（右下角）
                pdf.setFontSize(9);
                pdf.setTextColor(120);
                pdf.text(`Page ${pageNum}`, 195, 288, { align: "right" });

                // logo图片在右侧
                pdf.addImage(syngentaImg, "PNG", 170, 282.8, 24.6, 7.2);
                // 恢复默认颜色
                pdf.setTextColor(0);
            };
            pdf.setFont("Arial", "bold");
            pdf.setFontSize(18);
            pdf.text("Syngenta GreenCast Weather Modelling", 105, 18, { align: "center" });
            pdf.setFontSize(14);
            pdf.setFont("Arial", "italic");
            pdf.text("Implications of changing climate on your course", 105, 28, { align: "center" });
            pdf.setFont("Arial", "normal");
            // 第一部分
            pdf.setFontSize(16);
            pdf.text("Temperature trends for your course", 15, 40);
            pdf.setFontSize(12);
            pdf.text("Changing climate is the cause of greater weather extremes that challenge turf care and golf course management. The underlying trend that " +
                "is possibly having the greatest effects on turf health and quality is the continual rise in temperature.", 15, 48, { maxWidth: 180 });
            //原始chart尺寸2370，980，缩小0.08倍适应A4纸张
            if (imgData1) {
                pdf.addImage(imgData1, "PNG", 15, 60, 189.6, 78.4);
            }
            pdf.text("Weather records for your course track the rise that has been experienced over the past 10 years, and the implications if that trend continues " +
                "at the current pace for the coming years.", 15, 150, { maxWidth: 180 });
            addFooter(1);
            // 第二部分
            pdf.addPage();
            pdf.setFontSize(16);
            pdf.text("Rapid rise in summer temperatures", 15, 20);
            pdf.setFontSize(12);
            pdf.text("Turf quality can be severely impacted by effects of prolonged higher temperatures. New biostimulant, water management and turf health strategies" +
                " are essential to maintain turf through the stress of summer heat and weather extremes. Higher temperature will see turf diseases such as dollar spot, " +
                "anthracnose and grey leaf spot impacting more frequently and with greater severity.", 15, 28, { maxWidth: 180 });
            //原始chart尺寸1158，800，缩小0.16倍适应A4纸张,185.28,128
            let img2DrawHeight = 0;
            const img2DrawY = 48;
            if (imgData2) {
                // 等比例缩放插入，最大宽185.28mm，高128mm，居中
                await new Promise<void>(resolve => {
                    const img = new window.Image();
                    img.src = imgData2;
                    img.onload = () => {
                        const maxWidth = 185.28;
                        const maxHeight = 128;
                        const { naturalWidth: w, naturalHeight: h } = img;
                        const scale = Math.min(maxWidth / w, maxHeight / h, 1);
                        const drawW = w * scale;
                        const drawH = h * scale;
                        const imgX = (210 - drawW) / 2;
                        img2DrawHeight = drawH;
                        pdf.addImage(imgData2, "PNG", imgX, img2DrawY, drawW, drawH);
                        resolve();
                    };
                });
            }
            // 动态计算文本Y坐标，图片下方间距12mm
            const text2Y = img2DrawY + (img2DrawHeight > 0 ? img2DrawHeight + 12 : 137); // 137为原先185-48
            pdf.text(
                "Weather data for your course highlights the hours of temperature over 15 degrees Celsius that is the trigger point for many heat related issues and diseases. " +
                "The effects are already being experienced, but projected forward at current trends through the working lifetime, indicating just how serious issues could" +
                " become, and why actions need to be planned now.",
                15,
                text2Y,
                { maxWidth: 180 }
            );
            addFooter(2);
            // 第三部分
            pdf.addPage();
            pdf.setFontSize(16);
            pdf.text("Dramatic decline in disease-stopping cold", 15, 20);
            pdf.setFontSize(12);
            pdf.text("Temperatures below 2 degrees Celsius are crucial to stop the development of microdochium patch – the primary cause of winter disease damage to cool season turf." +
                " But the effects of a changing climate show that the number of hours under 2 degrees Celsius have declined significantly over recent years.", 15, 28, { maxWidth: 180 });
            let img3DrawHeight = 0;
            const img3DrawY = 40;
            if (imgData3) {
                // 等比例缩放插入，最大宽185.28mm，高128mm，居中
                await new Promise<void>(resolve => {
                    const img = new window.Image();
                    img.src = imgData3;
                    img.onload = () => {
                        const maxWidth = 185.28;
                        const maxHeight = 128;
                        const { naturalWidth: w, naturalHeight: h } = img;
                        const scale = Math.min(maxWidth / w, maxHeight / h, 1);
                        const drawW = w * scale;
                        const drawH = h * scale;
                        const imgX = (210 - drawW) / 2;
                        img3DrawHeight = drawH;
                        pdf.addImage(imgData3, "PNG", imgX, img3DrawY, drawW, drawH);
                        resolve();
                    };
                });
            }
            // 动态计算文本Y坐标，图片下方间距12mm
            const text3Y = (typeof img3DrawHeight === "number" && img3DrawHeight > 0 ? 40 + img3DrawHeight + 12 : 149); // 149为原先177-28
            pdf.text(
                "Tracking hourly weather data for your course shows just how few hours are now experienced in the average winter – which highlights why issues such as " +
                "microdochium are becoming increasingly severe. And if current trends are projected through the foreseeable future, cold weather periods to stop disease are going" +
                " to be even less frequent, and for many may never occur in an average year. Turf managers will need to adapt their turf disease management strategies to cope with " +
                "the new normal for warmer conditions.",
                15,
                text3Y,
                { maxWidth: 180 }
            );
            pdf.setFontSize(12);
            if (selectedCountry === "GB"|| selectedCountry === "IE") {
                const infoPrefix = "To find out more about the implications of climate change and solutions for turf management visit:";
                const urlText = "www.syngentaturf.co.uk.";
                const maxWidth = 180;
                const infoY = 264;
                const centerX = pdf.internal.pageSize.getWidth() / 2;
                const prefixStartX = 15;
                const lineHeight = pdf.getLineHeightFactor() * pdf.getFontSize() * 0.3528;

                // 核心修复：定义元组类型
                const linkBlue: [number, number, number] = [0, 0, 255];

                const prefixLines = pdf.splitTextToSize(infoPrefix, maxWidth);
                let currentY = infoY;

                prefixLines.forEach(line => {
                    pdf.text(line, prefixStartX, currentY, { align: "left" });
                    currentY += lineHeight;
                });

                const urlWidth = pdf.getTextWidth(urlText);
                const urlX = centerX - urlWidth / 2;

                // 扩展运算符可正常使用
                pdf.setTextColor(...linkBlue);
                pdf.textWithLink(urlText, urlX, currentY, { url: "https://www.syngentaturf.co.uk" });

                const underlineY = currentY + 1;
                const underlineStart = urlX;

                pdf.setDrawColor(...linkBlue);
                pdf.setLineWidth(0.2);
                pdf.line(underlineStart, underlineY, underlineStart + urlWidth, underlineY);

                // 恢复默认颜色
                pdf.setTextColor(0, 0, 0);
                pdf.setDrawColor(0, 0, 0);
            }
            addFooter(3);

            // 动态生成文件名
            let fileName;
            if (selectedLocationData?.source?.toLowerCase() === "postalcode") {
                fileName = `${selectedLocationData?.placeName} (${selectedLocationData?.postalCode}).pdf`;
            } else if (selectedLocationData?.source?.toLowerCase() === "placename") {
                fileName = `${selectedLocationData?.displayName} (${selectedLocationData?.postalCode}).pdf`;
            }
            else {
                fileName = `${selectedLocationData?.displayName}.pdf`;
            }
            pdf.save(fileName);
            dispatch(updateCommonSuccessMessage("PDF downloaded successfully."));
        } catch (error) {
            dispatch(updateCommonErrorMessage("PDF download failed, please try again!"));
            console.error("PDF download failed:", error);
        } finally {
            dispatch(updateDownloadAllLoading(false));
            //setLoading(false);
        }
    }, [chartResult, histogramRef, sup15CRef, sub2CRef, dispatch, activeTab, onTabChange]);
    return (
        <div style={{ position: "relative" }}>
            <Spin spinning={loading}>
                <Button
                    className="download-btn download-btn-icon-right pdf-download-btn-responsive"
                    onClick={handleDownloadPdf}
                    disabled={loading}
                >
                    <span className="download-btn-text">Download All</span>
                    <span className="download-btn-icon" style={loading ? { backgroundColor: "#8abf84" } : {}}><DownloadOutlined /></span>
                </Button>
            </Spin>
        </div>
    );
};

export default PdfExportDemo;